'use client';

import * as React from 'react';
import { ArrowUpRight, CreditCard, Repeat, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/ui/page-header';
import { TableCard } from '@/components/ui/table-card';
import { useSubscriptionsList, Subscription } from '@/lib/api/subscriptions';
import { SubscriptionManagerDialog } from '@/components/subscriptions/subscription-manager';
import { formatMoney } from "@/lib/money";
import { SubscriptionsTable } from './components/SubscriptionsTable';
import { ResourceStatsCardGrid } from '@/components/ui/resource-stats-card';
import { useTablePage } from '@/hooks/use-table-page';
import { subscriptionQueryParamsMapping, subscriptionsFilterMapping, subscriptionsSortMapping } from './utils/table-mappings';
import { useSubscriptionAction } from '@/hooks/use-subscription-action';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(SubscriptionsPage, PERMISSIONS.SUBSCRIPTIONS_VIEW);

function SubscriptionsPage() {
  const { t } = useTranslation(['subscriptions', 'common']);

  // Use generic table page hook
  const { searchQuery, setSearchQuery, tableState, setTableState, queryInput } = useTablePage({
    initialSorting: [{ id: 'createdAt', desc: true }],
    filterMapping: subscriptionsFilterMapping,
    sortMapping: subscriptionsSortMapping,
    queryParamsMapping: subscriptionQueryParamsMapping,
  });

  const {
    data: subscriptionsResponse,
    isLoading: loading,
    isFetching: fetching,
    error,
    refetch,
  } = useSubscriptionsList(queryInput);

  const subscriptions = subscriptionsResponse?.subscriptions || [];
  const total = subscriptionsResponse?.total || 0;

  // Extract options - ensure object reference changes when data changes
  const options = React.useMemo(() => {
    const backendOptions = subscriptionsResponse?.options;
    if (!backendOptions) {
      return {
        'subscriptions.status': [],
        'stores.id': [],
        preferredProcessorId: [],
      };
    }
    return {
      'subscriptions.status': backendOptions['subscriptions.status'] || [],
      'stores.id': backendOptions['stores.id'] || [],
      preferredProcessorId: backendOptions.preferredProcessorId || [],
    };
  }, [subscriptionsResponse]);

  // Memoized stats cards configuration
  const statsCards = React.useMemo(
    () => [
      {
        title: t('subscriptions:totalSubscriptions'),
        value: total,
        description: t('subscriptions:allTime'),
        icon: Repeat,
      },
      {
        title: t('subscriptions:activeSubscriptions'),
        value: subscriptions.filter((s: Subscription) => s.status === 'active').length,
        description: t('subscriptions:currentlyActive'),
        icon: CreditCard,
      },
      {
        title: t('subscriptions:monthlyRevenue'),
        value: formatMoney(
          subscriptions.reduce((acc: number, s: Subscription) => {
            if (s.status === 'active' && s.priceInterval === 'month') {
              return acc + (s.sortableAmount || 0) / 100; // Convert cents to dollars
            }
            return acc;
          }, 0),
          'USD',
        ),
        description: t('subscriptions:monthlyRecurring'),
        icon: ArrowUpRight,
      },
      {
        title: t('subscriptions:averageValue'),
        value:
          subscriptions.length === 0
            ? '-'
            : formatMoney(
              Math.round(
                subscriptions.reduce(
                  (acc: number, s: Subscription) => acc + (Number(s.sortableAmount) || 0),
                  0,
                ) / subscriptions.length,
              ) / 100, // Convert cents to dollars
              'USD',
            ),
        description: t('subscriptions:perSubscription'),
        icon: Users,
      },
    ],
    [subscriptions, total, t],
  );
  // Subscription action hooks
  const { cancelSubscription, resumeSubscription, pauseSubscription, isCanceling, isResuming, isPausing } =
    useSubscriptionAction();

  // Memoized event handlers
  const handleCancelSubscription = React.useCallback(
    (subscriptionId: string, cancelAtPeriodEnd = true) => {
      cancelSubscription(subscriptionId, cancelAtPeriodEnd);
    },
    [cancelSubscription],
  );

  const handleResumeSubscription = React.useCallback(
    (subscriptionId: string) => {
      resumeSubscription(subscriptionId);
    },
    [resumeSubscription],
  );

  const handlePauseSubscription = React.useCallback(
    (subscriptionId: string) => {
      pauseSubscription(subscriptionId);
    },
    [pauseSubscription],
  );

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      {/* Page header */}
      <PageHeader title={t('subscriptions:title')} description={t('subscriptions:description')}>
        <SubscriptionManagerDialog />
      </PageHeader>
      {/* Stats cards */}
      {/* <ResourceStatsCardGrid cards={statsCards} loading={loading} /> */}

      {/* Subscriptions table */}
      <TableCard>
        <SubscriptionsTable
          subscriptions={subscriptions}
          total={total}
          options={options}
          loading={loading || fetching}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onTableStateChange={setTableState}
          onCancelSubscription={handleCancelSubscription}
          onResumeSubscription={handleResumeSubscription}
          onPauseSubscription={handlePauseSubscription}
          isCanceling={isCanceling}
          isResuming={isResuming}
          isPausing={isPausing}
          onRefetch={refetch}
        />

        {/* Error message */}
        {error && (
          <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
            {error instanceof Error ? error.message : t('subscriptions:loadError')}
          </div>
        )}
      </TableCard>
    </div>
  );
}
